import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Trophy, Users, ChartLineUp, Handshake, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const getLevelBadgeClass = (level) => {
  switch (level) {
    case 'Elite':
      return 'bg-purple-100 text-purple-800';
    case 'Excelente':
      return 'bg-emerald-100 text-emerald-800';
    case 'Muito Bom':
      return 'bg-blue-100 text-blue-800';
    case 'Em Avaliação':
      return 'bg-amber-100 text-amber-800';
    case 'Baseline':
      return 'bg-sky-100 text-sky-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const getMedal = (index) => {
  if (index === 0) return '🥇';
  if (index === 1) return '🥈';
  if (index === 2) return '🥉';
  return index + 1;
};

const ComponentBar = ({ label, value, weight }) => {
  const safeValue = Number(value || 0);
  const percent = Math.max(0, Math.min(100, safeValue));

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-800">{safeValue.toFixed(1)} · peso {Math.round((weight || 0) * 100)}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-slate-700 rounded-full"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};


const KPICard = ({ title, value, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <h3 className="text-3xl font-bold text-slate-900 mt-1">{value}</h3>
      </div>
      <div className="p-3 rounded-xl bg-slate-100 text-slate-700">
        <Icon size={28} weight="duotone" />
      </div>
    </div>
  </div>
);

const PerformanceComercial = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPerformance = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/broker-performance`, {
        withCredentials: true,
      });
      setRows(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar performance comercial');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
  }, []);

  const calcProposalRate = (item) => {
    if (!item.total_leads) return 0;
    return ((item.total_proposals || 0) / item.total_leads * 100).toFixed(1);
  };

  const calcSalesRate = (item) => {
    if (!item.total_leads) return 0;
    return ((item.total_sales || 0) / item.total_leads * 100).toFixed(1);
  };

  const calcAutoReturnRate = (item) => {
    if (!item.total_leads) return 0;
    return ((item.auto_returns || 0) / item.total_leads * 100).toFixed(1);
  };

  const enriched = [...rows].map((item) => ({
    ...item,
    proposal_rate: Number(calcProposalRate(item)),
    sales_rate: Number(calcSalesRate(item)),
    auto_return_rate: Number(calcAutoReturnRate(item)),
  })).sort((a, b) => {
    if ((b.total_sales || 0) !== (a.total_sales || 0)) return (b.total_sales || 0) - (a.total_sales || 0);
    if (b.sales_rate !== a.sales_rate) return b.sales_rate - a.sales_rate;
    return (b.total_proposals || 0) - (a.total_proposals || 0);
  });

  const totals = rows.reduce((acc, item) => {
    acc.leads += item.total_leads || 0;
    acc.proposals += item.total_proposals || 0;
    acc.sales += item.total_sales || 0;
    acc.autoReturns += item.auto_returns || 0;
    return acc;
  }, { leads: 0, proposals: 0, sales: 0, autoReturns: 0 });

  if (loading) {
    return (
      <Layout>
        <div className="p-6 text-slate-600">Carregando performance...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Performance Comercial</h1>
            <p className="text-slate-600 mt-1">
              Indicadores acumulados por corretor para governança e futura distribuição inteligente.
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href="/lead-intake"
              className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 transition whitespace-nowrap"
            >
              Lead Intake →
            </a>
            <a
              href="/mdi-explain"
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition whitespace-nowrap"
            >
              MDI Explain →
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <KPICard title="Leads Distribuídos" value={totals.leads} icon={Users} />
          <KPICard title="Propostas" value={totals.proposals} icon={Handshake} />
          <KPICard title="Vendas" value={totals.sales} icon={Trophy} />
          <KPICard title="Retornos para IA" value={totals.autoReturns} icon={WarningCircle} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center gap-2">
            <ChartLineUp size={24} className="text-slate-700" />
            <div>
              <h2 className="font-bold text-slate-900">Ranking de Performance</h2>
              <p className="text-sm text-slate-500">Ordenado por vendas, taxa de conversão e propostas</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-5 py-3">#</th>
                  <th className="text-left px-5 py-3">Corretor</th>
                  <th className="text-right px-5 py-3">Leads</th>
                  <th className="text-right px-5 py-3">Propostas</th>
                  <th className="text-right px-5 py-3">Vendas</th>
                  <th className="text-right px-5 py-3">Retornos IA</th>
                  <th className="text-right px-5 py-3">IPC</th>
                  <th className="text-left px-5 py-3">IPC Explain</th>
                </tr>
              </thead>
              <tbody>
                {enriched.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-5 py-8 text-center text-slate-500">
                      Nenhum dado de performance encontrado.
                    </td>
                  </tr>
                ) : (
                  enriched.map((item, index) => (
                    <tr key={item.broker_id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                      <td className="px-5 py-4 font-semibold text-slate-700 text-lg">{getMedal(index)}</td>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{item.broker_name || 'Corretor'}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Venda {item.sales_rate || 0}% · Proposta {item.proposal_rate || 0}%
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">{item.total_leads || 0}</td>
                      <td className="px-5 py-4 text-right">{item.total_proposals || 0}</td>
                      <td className="px-5 py-4 text-right font-semibold">{item.total_sales || 0}</td>
                      <td className="px-5 py-4 text-right">{item.auto_returns || 0}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="text-2xl font-bold text-slate-900">{item.ipc_score ?? '--'}</div>
                        <span className={`inline-flex mt-1 px-3 py-1 rounded-full text-xs font-semibold ${getLevelBadgeClass(item.ipc_level)}`}>
                          {item.ipc_level || 'Baseline'}
                        </span>
                      </td>
                      <td className="px-5 py-4 min-w-[320px]">
                        <div className="space-y-3">
                          {item.ipc_status === 'baseline' ? (
                            <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
                              Corretor sem histórico comercial suficiente. O IPC será calculado após os primeiros leads.
                            </div>
                          ) : (
                            <>
                              <ComponentBar label="Conversão em venda" value={item.ipc_components?.conversion ?? item.sales_score} weight={item.ipc_weights?.conversion ?? 0.4} />
                              <ComponentBar label="Propostas" value={item.ipc_components?.proposal ?? item.proposal_score} weight={item.ipc_weights?.proposal ?? 0.2} />
                              <ComponentBar label="Tempo de resposta" value={item.ipc_components?.response ?? item.response_score} weight={item.ipc_weights?.response ?? 0.2} />
                              <ComponentBar label="Governança" value={item.ipc_components?.governance ?? item.process_score} weight={item.ipc_weights?.governance ?? 0.1} />
                              <ComponentBar label="Recuperação" value={item.ipc_components?.recovery ?? item.recovery_score} weight={item.ipc_weights?.recovery ?? 0.1} />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PerformanceComercial;
